// Centralized design tokens for Modules 1 & 2 redesign.
// Use these instead of hardcoded hex values inside screens.

export const palette = {
  brand: '#F14950',
  brandDark: '#D83A40',
  brandSoft: '#FFE9EA',
  brandSofter: '#FFF5F5',

  ink: '#0F1729',
  inkSoft: '#3A4256',
  muted: '#6B7280',
  hint: '#9AA1AE',
  line: '#E5E7EB',
  surface: '#FFFFFF',
  surfaceAlt: '#FFF8F8',
  surfaceCanvas: '#FFF5F5',

  success: '#10B981',
  successSoft: '#DCFCE7',
  warning: '#F59E0B',
  warningSoft: '#FEF3C7',
  info: '#3B82F6',
  infoSoft: '#DBEAFE',
  danger: '#EF4444',
  dangerSoft: '#FEE2E2',
  purple: '#8B5CF6',
  purpleSoft: '#EDE9FE',
  pink: '#EC4899',
  pinkSoft: '#FCE7F3',
  teal: '#14B8A6',
  tealSoft: '#CCFBF1',
};

// Source-specific colors for Lead source chips.
// All on-brand: brand red for source-driven entries, neutral for manual entries.
export const sourceColors = {
  FORM:        { fg: '#F14950', bg: '#FFE9EA', icon: 'description' },
  GIVEAWAY:    { fg: '#F14950', bg: '#FFE9EA', icon: 'emoji_events' },
  LEAD_MAGNET: { fg: '#F14950', bg: '#FFE9EA', icon: 'redeem' },
  MANUAL:      { fg: '#374151', bg: '#F3F4F6', icon: 'person' },
};

// Status colors for entity status pills.
export const statusColors = {
  DRAFT:     { fg: '#6B7280', bg: '#F3F4F6' },
  PUBLISHED: { fg: '#065F46', bg: '#DCFCE7' },
  ACTIVE:    { fg: '#065F46', bg: '#DCFCE7' },
  ENDED:     { fg: '#1E3A8A', bg: '#DBEAFE' },
  CANCELLED: { fg: '#9F1239', bg: '#FEE2E2' },
  ARCHIVED:  { fg: '#9F1239', bg: '#FEE2E2' },
  PENDING:   { fg: '#92400E', bg: '#FEF3C7' },
  CLEAN:     { fg: '#065F46', bg: '#DCFCE7' },
  INFECTED:  { fg: '#9F1239', bg: '#FEE2E2' },
  SCAN_SKIPPED: { fg: '#374151', bg: '#F3F4F6' },
  SENT:      { fg: '#065F46', bg: '#DCFCE7' },
  FAILED:    { fg: '#9F1239', bg: '#FEE2E2' },
  DM_FALLBACK_EMAIL: { fg: '#1E3A8A', bg: '#DBEAFE' },
  VERIFIED:  { fg: '#065F46', bg: '#DCFCE7' },
  REJECTED:  { fg: '#9F1239', bg: '#FEE2E2' },
  UNVERIFIED_MANUAL_REVIEW: { fg: '#92400E', bg: '#FEF3C7' },
};

// ----- Chart palette -----
//
// Derived from the tokens above and VALIDATED, not eyeballed. The order below is
// the one that maximised the worst *adjacent* colour-vision-deficiency separation
// across all 720 brand-led orderings of our seven chart hues, measured as
// Euclidean distance in OKLab x100 under protanopia and deuteranopia:
//
//   adjacent pairs (bars, lines, stacks)  worst ΔE 14.1  — passes
//   all pairs      (donut segments)       first THREE slots only
//
// That second limit is load-bearing rather than cautious. In a donut any two
// segments can end up side by side, and a fourth slot puts purple next to blue
// at ΔE 1.3 under deuteranopia — indistinguishable — and 12.0 even with full
// colour vision. So a donut shows at most three categories and folds the rest
// into `other`; it is not fixable by re-ordering.
//
// Slots are assigned IN ORDER and never cycled. An eighth series is not a new
// hue: it folds into `other` too. A generated hue is indistinguishable from one
// already on screen, which is the whole reason the order is fixed.
//
// `#10B981`, `#14B8A6` and `#F59E0B` sit below 3:1 against white. That is
// allowed here only because every chart using them also carries visible labels
// and a readable row of numbers — the value is never reachable by colour alone.
export const chartPalette = {
  categorical: ['#F14950', '#3B82F6', '#10B981', '#8B5CF6', '#14B8A6', '#F59E0B', '#EC4899'],
  //: All-pairs forms (donut). Three, for the reason above.
  maxSlices: 3,
  //: Adjacent forms (bars, lines, stacks).
  maxSeries: 7,
  //: De-emphasis. Deliberately below the chroma floor — it is not an identity
  //: colour, it is the absence of one, which is exactly what "Other" means.
  other: '#6B7280',
  //: Magnitude, one hue light -> dark. Never a rainbow: a multi-hue ramp makes
  //: the reader decode a legend to answer "which is bigger".
  //:
  //: Validated as an ORDINAL ramp, which is a different test from the
  //: categorical one above — monotone lightness, adjacent steps far enough
  //: apart to be told apart, and a light end that is still visible against the
  //: surface. The obvious ramp (a tint of the brand fading to #FFD3D5) failed
  //: twice over: its lightest step sat at 1.35:1 on white, so "a little
  //: traffic" was indistinguishable from "none", and its two darkest steps were
  //: 0.058 apart in lightness when 0.06 is the floor. This one keeps the brand
  //: red as its middle step and clears both.
  sequential: ['#F79AA0', '#F4767C', '#F14950', '#D2353B', '#A82A2F'],
  //: Not a step of the ramp, deliberately. "We have no data for this country"
  //: is a different statement from "this country is at the bottom of the
  //: scale", and a pale tint of the ramp would say the second.
  noData: '#EDEFF2',
  //: The magnitude fill behind a ranked row. A wash rather than a saturated
  //: slab: a 6px bar of full-strength brand red spanning the card reads as a
  //: rule drawn under the label, and at 100% it reads as nothing at all. The
  //: row itself carries the value instead, and the text sits on top of it.
  rowFill: 'rgba(241, 73, 80, 0.13)',
  rowFillHover: 'rgba(241, 73, 80, 0.22)',
  //: The unfilled remainder of that row. Barely there, but it gives every row
  //: the same defined shape — without it a 3% value is a stray sliver floating
  //: behind its label rather than a small part of a whole.
  rowTrack: 'rgba(241, 73, 80, 0.04)',
  //: Chrome. One step off the surface, hairline, solid — never dashed.
  grid: '#EDEFF2',
  axis: '#9AA1AE',
};

// The two series plotted together on the link charts. Same unit (a count of
// people), so they share ONE axis — two scales on one plot is the single most
// misread thing a dashboard can do.
export const chartSeries = {
  clicks: { key: 'clicks', label: 'Clicks', color: '#F14950' },
  uniqueVisitors: { key: 'uniqueVisitors', label: 'Unique visitors', color: '#3B82F6' },
};

export const radius = {
  xs: 6,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 22,
  pill: 999,
};

export const shadow = {
  card:   '0 1px 2px rgba(15, 23, 41, 0.04), 0 1px 3px rgba(15, 23, 41, 0.06)',
  hover:  '0 8px 24px rgba(15, 23, 41, 0.08), 0 2px 6px rgba(15, 23, 41, 0.04)',
  modal:  '0 24px 48px rgba(15, 23, 41, 0.12), 0 6px 16px rgba(15, 23, 41, 0.08)',
  inset:  'inset 0 0 0 1px rgba(15, 23, 41, 0.06)',
  glow:   '0 0 0 4px rgba(241, 73, 80, 0.12)',
};

// All module gradients use the brand red so the theme stays consistent.
export const gradients = {
  brand: 'linear-gradient(135deg, #F14950 0%, #FF7A82 100%)',
  hero:  'linear-gradient(135deg, #FFE9EA 0%, #FFF5F5 50%, #FFFFFF 100%)',
  forms:     'linear-gradient(135deg, #FFE9EA 0%, #FFF5F5 100%)',
  magnets:   'linear-gradient(135deg, #FFE9EA 0%, #FFF5F5 100%)',
  giveaways: 'linear-gradient(135deg, #FFE9EA 0%, #FFF5F5 100%)',
  leads:     'linear-gradient(135deg, #FFE9EA 0%, #FFF5F5 100%)',
  linkinbio: 'linear-gradient(135deg, #FFE9EA 0%, #FFF1F2 100%)',
};

export const typography = {
  pageTitle:    { fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: palette.ink },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: palette.ink },
  cardTitle:    { fontSize: 15, fontWeight: 700, color: palette.ink },
  body:         { fontSize: 14, color: palette.inkSoft },
  caption:      { fontSize: 12, color: palette.muted },
  micro:        { fontSize: 11, color: palette.hint, letterSpacing: '0.04em', textTransform: 'uppercase' },
  // Small all-caps label used above field groups and metric values.
  eyebrow:      { fontSize: 11, fontWeight: 700, color: palette.muted, letterSpacing: '0.07em', textTransform: 'uppercase' },
};

// Shared easing/duration so every hover, expand and transition in the product
// moves the same way. `base` is the default for colour + shadow changes,
// `emphasis` for anything that also moves in space.
export const motion = {
  fast:     '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  base:     '180ms cubic-bezier(0.4, 0, 0.2, 1)',
  emphasis: '260ms cubic-bezier(0.16, 1, 0.3, 1)',
};

// Keyboard-visible focus ring. Applied via `&:focus-visible` so pointer users
// never see it but keyboard users always do.
export const focusRing = {
  outline: 'none',
  boxShadow: `0 0 0 2px ${palette.surface}, 0 0 0 4px rgba(241, 73, 80, 0.45)`,
};

// Height of the fixed app bar — sticky page chrome offsets against this so it
// docks *below* the navbar instead of sliding underneath it.
export const APP_BAR_OFFSET = { xs: 56, sm: 64 };
